import { memo, useEffect, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChatView } from '../chat/ChatView';
import { BuyingPowerDots } from '../chat/fanBlocks';
import { aiStatusLabel, money } from '../chat/format';
import { AccountChatsDeps } from '../chats/AccountChatsDeps';
import { SideColumn } from '../chats/SideColumn';
import { useSwipeBack } from '../chats/phone/useSwipeBack';
import { usePinnedChatScreen } from '../chats/phone/usePinnedChatScreen';
import { FeatherIcon } from '../components/FeatherIcon';
import type { CrmAccount } from '../crm/crmAccess';
import type { AccountColour } from './colour';
import { FanslyChatPane } from './FanslyChatPane';
import { Avatar, FanAvatar } from './widgets/Avatar';

export interface OpenChat {
  accountId: string;
  fanId: string;
  fanName?: string | null;
  fanUsername?: string | null;
  fanAvatar?: string | null;
}

interface Props {
  chat: OpenChat | null;
  account: CrmAccount | null;
  colour: AccountColour;
  myUsername: string;
  liveTick: number;
  aiStatus: string | null;
  pinned: boolean;
  canBack: boolean;
  canForward: boolean;
  onBack: () => void;
  onForward: () => void;
  onTogglePin: () => void;
  /** Phones: back to the chats list (the chat covers it). */
  onList?: () => void;
  /** Dragging the fan header onto Pinned pins the chat. */
  onFanDragStart?: (e: PointerEvent, fan: { name: string | null; username: string | null; avatar: string | null }) => void;
  /** The side column opens a chat (this Account or another) here. */
  onOpenChat: (chat: OpenChat) => void;
  /** CRM id of the person signed in (the side column's browser-storage scope). */
  crmUserId: string;
  /** Phones: the side column opens as a sheet from the header. */
  compact?: boolean;
}

/** Memoized: the page re-renders on every live tick, the open chat only when its own props change. */
export const ChatPane = memo(function ChatPane({ chat, account, colour, myUsername, liveTick, aiStatus, pinned, canBack, canForward, onBack, onForward, onTogglePin, onList, onFanDragStart, onOpenChat, crmUserId, compact = false }: Props) {
  const { t } = useTranslation('workspace');
  const { t: tc } = useTranslation('chat');
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Phones: drag the chat right to go back to the list (ADR 0035, as on the Chats page).
  const swipe = useSwipeBack(compact && chat && onList ? onList : null);
  usePinnedChatScreen(!!(compact && chat));

  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => !menuRef.current?.contains(e.target as Node) && setMenu(false);
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [menu]);

  if (!chat || !account) {
    return (
      <div className="ws-pane ws-pane--empty">
        <FeatherIcon name="message-circle" size={26} />
        <b>{t('pane.emptyTitle')}</b>
        <span>{t('pane.emptyText')}</span>
      </div>
    );
  }

  const modelName = account.profile_name || account.username;
  const fansly = account.platform.trim().toLowerCase() === 'fansly';

  return (
    <div
      className={`ws-pane ${swipe.dragging ? 'is-dragging' : ''} ${swipe.leaving ? 'is-leaving' : ''}`}
      style={swipe.dx && !swipe.leaving ? ({ '--cp-chat-x': `${swipe.dx}px` } as React.CSSProperties) : undefined}
      {...swipe.handlers}
    >
      <div className="ws-pane-bar" style={{ background: colour.accent }} />
      <div className="ws-pane-model" style={{ background: colour.tint }}>
        {onList && (
          <button type="button" className="ws-nav-btn ws-nav-btn--list" onClick={onList} title={t('pane.toList')} aria-label={t('pane.toList')}>
            <FeatherIcon name="list" size={15} />
          </button>
        )}
        <button type="button" className="ws-nav-btn" disabled={!canBack} onClick={onBack} title={t('pane.back')}>
          <FeatherIcon name="arrow-left" size={15} />
        </button>
        <button type="button" className="ws-nav-btn" disabled={!canForward} onClick={onForward} title={t('pane.forward')}>
          <FeatherIcon name="arrow-right" size={15} />
        </button>
        <Avatar src={account.avatar_url} name={modelName} size={30} className="ws-pane-model-ini" ring={`0 0 0 2px ${colour.accent}`} />
        <span className="ws-pane-writing">
          <span className="ws-pane-writing-label">{t('pane.writingAs')}</span>
          <span className="ws-pane-writing-name">
            <b style={{ color: colour.accent }}>{modelName}</b>
            <span>{t(fansly ? 'pane.platformFansly' : 'pane.platform', { handle: account.username })}</span>
          </span>
        </span>
        <span className="ws-flex" />
        <span className="ws-ai-pill">
          <FeatherIcon name="bot" size={13} />
          {aiStatusLabel(aiStatus, tc)}
        </span>
      </div>
      {fansly ? (
        <FanslyChatPane chat={chat} liveTick={liveTick} pinned={pinned} onTogglePin={onTogglePin} onList={onList} compact={compact} />
      ) : (
      /* The Chats page's side column (the person's own blocks and widgets), with that page's dependencies for the open chat's Account. */
      <AccountChatsDeps accountId={chat.accountId} crmUserId={crmUserId}>
      <ChatView
        key={`${chat.accountId}:${chat.fanId}`}
        accountId={chat.accountId}
        fanId={chat.fanId}
        modelName={modelName}
        myUsername={myUsername}
        liveTick={liveTick}
        aiStatus={aiStatus}
        fanNameHint={chat.fanName}
        compact={compact}
        renderSide={(fan, frame) => (
          <SideColumn
            accountId={chat.accountId}
            fanId={chat.fanId}
            fan={fan}
            frame={frame}
            aiStatus={aiStatus}
            onOpenChat={(accountId, fanId, fanName) => onOpenChat({ accountId, fanId, fanName })}
          />
        )}
        renderHeader={(fan, controls) => (
          <div className="ws-pane-fan">
            <span
              className="ws-pane-fan-drag"
              title={onFanDragStart ? t('pane.dragToPin') : undefined}
              onPointerDown={(e) => onFanDragStart?.(e, { name: fan.profile?.name || chat.fanName || null, username: fan.profile?.username || chat.fanUsername || null, avatar: fan.profile?.avatar || chat.fanAvatar || null })}
            >
              <FanAvatar accountId={chat.accountId} src={fan.profile?.avatar || chat.fanAvatar} name={fan.profile?.name || chat.fanName} size={36} className="ws-pane-fan-ini" />
              <span className="ws-pane-fan-names">
                <b>{fan.profile?.name || chat.fanName || chat.fanId}</b>
                <span>{fan.profile?.username || chat.fanUsername ? `@${fan.profile?.username || chat.fanUsername}` : ''}</span>
              </span>
            </span>
            <span className="ws-pane-fan-stats">
              <BuyingPowerDots value={fan.buyingPower} size={6} />
              {fan.profile?.spent.total != null && <b>{money(fan.profile.spent.total)}</b>}
              {fan.lists
                .filter((l) => fan.memberOf.has(l.id))
                .slice(0, 2)
                .map((l) => (
                  <span key={l.id} className="ws-tag">
                    {l.name}
                  </span>
                ))}
            </span>
            <span className="ws-flex" />
            <button type="button" className={`ws-pin-btn ${pinned ? 'is-on' : ''}`} onClick={onTogglePin} title={pinned ? t('pane.unpin') : t('pane.pin')}>
              <FeatherIcon name="pin" size={15} />
            </button>
            {controls.side && (
              <button type="button" className={`ws-pin-btn ${controls.side.open ? 'is-on' : ''}`} onClick={controls.side.toggle} title={tc('fan.info')} aria-label={tc('fan.info')} aria-pressed={controls.side.open}>
                <FeatherIcon name="panel-right-open" size={15} />
              </button>
            )}
            <div className="ws-rel" ref={menuRef}>
              <button type="button" className="ws-pin-btn" onClick={() => setMenu((v) => !v)} title={t('pane.more')}>
                <FeatherIcon name="ellipsis" size={15} />
              </button>
              {menu && (
                <div className="ws-menu ws-menu--right">
                  <button
                    type="button"
                    className="ws-menu-item"
                    onClick={() => navigate(`/account/${encodeURIComponent(chat.accountId)}/crm/chats?fan=${encodeURIComponent(chat.fanId)}`)}
                  >
                    <FeatherIcon name="external-link" size={14} />
                    <span className="ws-menu-label">{t('pane.openInCrm')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      />
      </AccountChatsDeps>
      )}
    </div>
  );
});
